// AUPRÉA — Étape 15 : vue d'ensemble des bilans (admin)
import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { C, Spinner } from "./shared";
import { getB, Card, Stat, pctTxt, th, td } from "./bilansShared";

const Graph = ({ data, color, testid }) => (
  <div data-testid={testid} style={{ height: 220 }}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 30 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.mid }} interval={0} angle={-25} textAnchor="end" />
        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: C.mid }} />
        <Tooltip formatter={(v) => [v, "Bilans"]} />
        <Bar dataKey="n" fill={color} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const ScoreBloc = ({ titre, s, testid }) => (
  <Card title={titre} testid={testid}>
    <div style={{ fontSize: 26, fontFamily: "'Playfair Display', serif", color: C.navy }} data-testid={`${testid}-global`}>{s?.global == null ? "—" : `${s.global} %`}</div>
    <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>{s?.n || 0} bilan(s) avec réponses</div>
    {Object.entries(s?.sections || {}).map(([k, v]) => (
      <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginBottom: 4 }}>
        <span style={{ width: 72, color: C.mid }}>{k}</span>
        <div style={{ flex: 1, height: 8, background: C.gray, borderRadius: 4 }}><div style={{ width: `${v}%`, height: 8, borderRadius: 4, background: v >= 80 ? C.teal : v >= 50 ? C.amber : C.red }} /></div>
        <span style={{ width: 40, textAlign: "right" }}>{v} %</span>
      </div>
    ))}
  </Card>
);

export const VueEnsemble = ({ f }) => {
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); getB("/admin/bilans-kpi/overview", f).then(setD); }, [f]);
  if (!d) return <Spinner />;
  const evo = d.evolution == null ? (d.precedent == null ? "Choisissez une période pour comparer" : "Aucun bilan sur la période précédente") : `${d.evolution > 0 ? "+" : ""}${pctTxt(d.evolution)} vs période précédente (${d.precedent})`;
  const fu = d.funnel;
  return (
    <div data-testid="bilans-vue" style={{ display: "grid", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
        <Stat label="Bilans réalisés" value={d.total} sub={evo} testid="kpi-total" />
        <Stat label="Âge moyen" value={d.age_moyen == null ? "—" : `${String(d.age_moyen).replace(".", ",")} ans`} color={C.gold} testid="kpi-age" />
        <Stat label="Score moyen global" value={d.score_global == null ? "—" : `${d.score_global} %`} color={C.teal} testid="kpi-score" />
        <Stat label="Taux de transformation" value={pctTxt(fu.taux_transformation)} sub={`${fu.payes} payé(s) sur ${fu.bilans}`} color={C.purp} testid="kpi-taux" />
        <Stat label="Non attribués" value={d.non_attribues} color={C.red} testid="kpi-non-attribues" />
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {d.par_parcours.map(p => <span key={p.type} data-testid={`kpi-parcours-${p.type}`} style={{ background: C.navylt, color: C.navy, borderRadius: 999, padding: "5px 12px", fontSize: 12 }}>{p.label} : <strong>{p.n}</strong></span>)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14 }}>
        <Card title="Répartition par tranche d'âge"><Graph data={d.tranches} color={C.gold} testid="graph-tranches" /></Card>
        <Card title="Répartition par territoire"><Graph data={d.territoires} color={C.teal} testid="graph-territoires" /></Card>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 }}>
        <ScoreBloc titre="Bilan Succession · 3 piliers" s={d.scores.bilan_succession} testid="score-succession" />
        <ScoreBloc titre="Bilan Dirigeants · 5 sections" s={d.scores.bilan_dirigeants} testid="score-dirigeants" />
        <Card title="Ouvrir une succession" testid="kpi-complexite">
          <div style={{ fontSize: 26, fontFamily: "'Playfair Display', serif", color: C.navy }}>{d.complexite.label || "—"}</div>
          <div style={{ fontSize: 11, color: C.muted }}>Niveau de complexité moyen ({d.complexite.moyenne ?? "—"} / 3) · {d.complexite.n} bilan(s)</div>
        </Card>
        <Card title="Foncier Clair" testid="kpi-foncier">
          <div style={{ fontSize: 26, fontFamily: "'Playfair Display', serif", color: C.navy }}>{pctTxt(d.foncier.pct_reunies)}</div>
          <div style={{ fontSize: 11, color: C.muted }}>« Conditions a priori réunies » · {d.foncier.reunies} sur {d.foncier.n}</div>
        </Card>
      </div>
      <Card title="Transformation · bilans → propositions envoyées → payés" testid="kpi-funnel">
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
          <thead><tr>{["Bilans", "Propositions envoyées", "Payés", "Taux de proposition", "Taux de transformation"].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody><tr><td style={td}>{fu.bilans}</td><td style={td}>{fu.propositions}</td><td style={td}>{fu.payes}</td><td style={td}>{pctTxt(fu.taux_proposition)}</td><td style={td} data-testid="funnel-taux">{pctTxt(fu.taux_transformation)}</td></tr></tbody>
        </table>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Le détail par consultant est dans l'onglet « Par consultant ».</div>
      </Card>
    </div>
  );
};
